'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Smartphone, Shield, TrendingUp, LucideIcon } from 'lucide-react';

interface ValueProp {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
  iconBg: string;
  iconColor: string;
}

const valueProps: ValueProp[] = [
  {
    id: 'mobile-first',
    icon: Smartphone,
    title: 'Mobile-First Design',
    description:
      "Built for Africa's mobile money ecosystem with seamless M-Pesa, Airtel, and other provider integration",
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
  },
  {
    id: 'enterprise-security',
    icon: Shield,
    title: 'Enterprise Security',
    description:
      'Bank-level encryption, audit trails, and compliance features to protect your operations',
    iconBg: 'bg-accent/10',
    iconColor: 'text-accent',
  },
  {
    id: 'scalable-architecture',
    icon: TrendingUp,
    title: 'Scalable Architecture',
    description:
      'From small teams to large organizations, our platform grows with your needs',
    iconBg: 'bg-secondary/10',
    iconColor: 'text-secondary',
  },
];

function ValueCard({
  item,
  index,
  visible,
}: {
  item: ValueProp;
  index: number;
  visible: boolean;
}) {
  const Icon = item.icon;
  return (
    <div
      className={`relative flex flex-col bg-card rounded-xl border border-border p-6 shadow-card hover:shadow-card-hover transition-all duration-500 hover:-translate-y-1 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: visible ? `${index * 150}ms` : '0ms' }}
    >
      <div
        className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${item.iconBg} transition-transform duration-300 group-hover:scale-110`}
      >
        <Icon size={28} className={item.iconColor} />
      </div>

      <h3 className="text-lg font-700 text-foreground mb-2">{item.title}</h3>

      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed flex-1">
        {item.description}
      </p>
    </div>
  );
}

export default function TestimonialsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="py-16 sm:py-20 lg:py-24 bg-background"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-12 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-700 text-foreground">
            Why Organizations Choose Us
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">
            Built for African businesses, designed for scale
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {valueProps.map((item, i) => (
            <ValueCard key={item.id} item={item} index={i} visible={visible} />
          ))}
        </div>
      </div>
    </section>
  );
}
